import {HTMLReactParserOptions, Element as ParserElement, DOMNode, domToReact} from 'html-react-parser';
import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    SessionStatus,
    Timer as TimerComponent,
    Button,
    Icon,
    UserPreview,
    NodeElement,
    ClientDynamicContent
} from 'components';
import {DynamicComponentNames, OnHoldUnits as Units, ON_HOLD_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {dynamicContentUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

import './styles.scss';

const T_KEYS_CLIENT_IN_SESSION_PAGE = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.ON_HOLD_VIEW;

const {isMatchElement, nodeElementParent, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        yourAgentLabel, agent, agentName, statusDate, endSession, endSessionBtnLabel,
        description, subDescription, localParticipant
    } = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            const showAgentData = agent?.data;

            if (isMatchElement(Units.AgentInfo, node)) {
                return nodeElementParent({show: showAgentData});
            }
            if (isMatchElement(Units.YourAgentLabel, node)) {
                return <NodeElement show={showAgentData} node={node}>{yourAgentLabel}</NodeElement>;
            }
            if (isMatchElement(Units.AgentPreview, node)) {
                return <NodeElement show={showAgentData} node={node}><UserPreview name={agentName} /></NodeElement>;
            }
            if (isMatchElement(Units.AgentName, node)) {
                return <NodeElement show={showAgentData} node={node}>{agentName}</NodeElement>;
            }
            if (isMatchElement(Units.StatusLabel, node)) {
                return (
                    <NodeElement node={node}>
                        <SessionStatus status='awaiting-hold' size='large' />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Timer, node)) {
                return (
                    <TimerComponent
                        config={{optionalHours: true, minutes: true, seconds: true}}
                        startDate={new Date(statusDate)}
                    >
                        {({time}) => <NodeElement node={node}>{time}</NodeElement>}
                    </TimerComponent>
                );
            }
            if (isMatchElement(Units.Icon, node)) {
                return <NodeElement node={node}><Icon name='pause' size='2xl' color='default-dimmed' /></NodeElement>;
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.SubDescription, node)) {
                return <NodeElement node={node}>{subDescription}</NodeElement>;
            }
            if (isMatchElement(Units.LeaveSessionButton, node)) {
                return <NodeElement node={node} onClick={endSession}>{domToReact(node.children as DOMNode[])}</NodeElement>;
            }
            if (isMatchElement(Units.LeaveSessionButtonLabel, node)) {
                return <NodeElement node={node}>{endSessionBtnLabel}</NodeElement>;
            }
            if (isMatchElement(Units.LeaveSessionButtonDefault, node)) {
                return (
                    <NodeElement node={node}>
                        <Button
                            color='attention'
                            width='auto'
                            height='auto'
                            rounded
                            onClick={endSession}
                        >
                            <Icon name='phone-end' size='4xl' />
                        </Button>
                    </NodeElement>
                );
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const OnHoldView: React.FC = () => {
    const {t} = useTranslation();
    const {endLocalSession} = useContext(ClientContext);
    const {agent, image, statusDate, onHoldImage} = useClientSession();

    const yourAgentLabel = useMemo(() => t(T_KEYS_CLIENT_IN_SESSION_PAGE.YOUR_AGENT_IS), [t]);
    const agentName = useMemo(() => getParticipantName(agent?.data), [agent]);

    return (
        <ClientDynamicLayout image={onHoldImage || image}>
            <ClientDynamicContent
                component={DynamicComponentNames.OnHold}
                defaultTemplate={ON_HOLD_DEFAULT_TEMPLATE}
                optionsProps={{
                    agent, yourAgentLabel, agentName, statusDate,
                    description: t(T_KEYS.DESCRIPTION),
                    subDescription: t(T_KEYS.SUB_DESCRIPTION),
                    endSessionBtnLabel: t(T_KEYS_CLIENT_IN_SESSION_PAGE.END_CALL),
                    endSession: endLocalSession
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};
